import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import type { BrandingNormalized } from '@/lib/branding/types';
import { bootstrapPublicBranding } from '@/lib/branding/bootstrapPublicBranding';
import { applyBrandingToDom } from '@/lib/branding/applyBrandingToDom';
import { subscribeSessionEvents } from '@/lib/auth/sessionCoordinator';

interface PublicBrandingContextType {
  branding: BrandingNormalized;
  isLoading: boolean;
  refresh: () => Promise<void>;
}

const PublicBrandingContext = createContext<PublicBrandingContextType | undefined>(undefined);

/**
 * PublicBrandingProvider - Single source of truth for branding
 * 
 * Flow:
 * 1. Initialize with provided initial branding (from bootstrap)
 * 2. Apply to DOM immediately via BrandingDomApplier
 * 3. Optionally refresh from API
 */
export const PublicBrandingProvider: React.FC<{
  children: ReactNode;
  initial: BrandingNormalized;
}> = ({ children, initial }) => {
  const [branding, setBranding] = useState<BrandingNormalized>(initial);
  const [isLoading, setIsLoading] = useState(false);

  const refresh = async () => {
    setIsLoading(true);
    try {
      const result = await bootstrapPublicBranding();
      setBranding(result.branding);
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('[PublicBrandingProvider] Failed to refresh branding:', error);
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const unsub = subscribeSessionEvents((event) => {
      if (event.type === 'RECOVERED') {
        refresh().catch(() => {
          // ignore
        });
      }
    });
    return unsub;
  }, []);

  return (
    <PublicBrandingContext.Provider value={{ branding, isLoading, refresh }}>
      {children}
    </PublicBrandingContext.Provider>
  );
};

/**
 * Hook to access public branding context
 */
export const usePublicBranding = (): PublicBrandingContextType => {
  const context = useContext(PublicBrandingContext);
  if (!context) {
    throw new Error('usePublicBranding must be used within PublicBrandingProvider');
  }
  return context;
};

export const useOptionalPublicBranding = (): PublicBrandingContextType | null => {
  return useContext(PublicBrandingContext) ?? null;
};

/**
 * BrandingDomApplier - Component that applies branding to DOM
 * DUY NHẤT 1 NƠI mutate DOM
 * Uses useEffect to apply branding whenever it changes (idempotent)
 */
export const BrandingDomApplier: React.FC = () => {
  const { branding } = usePublicBranding();

  useEffect(() => {
    applyBrandingToDom(branding);
  }, [branding]);

  return null; // This component doesn't render anything
};

